import { DatePipe } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, catchError, of, forkJoin } from 'rxjs';
import { AuthService } from '../../auth';
import {
  PatientModel,
  PatientSessionModel,
  PatientTargetModel,
  PatientBehaviorModel,
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  PatientBehaviorLocationModel,
  PatientBehaviorResponseModel,
  PatientBehaviorSeverityModel,
  PromptCodeModel,
  PatientProgramModel,
} from '../../core/api/models';
import {
  HealthCheckApiService,
  SupervisorApiService,
  StaffApiService,
} from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { SupervisorHelperService } from '../../supervisor/supervisor.helper.service';
import { PatientSessionService } from '../home/patient-session.service';
import { inject, DestroyRef, signal } from '@angular/core';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';

export class SessionsBaseComponent {
  isLoading: boolean = false;

  selectedPatient: PatientModel | null = null;

  private destroyRef = inject(DestroyRef);
  private patientDataFacade = inject(PatientDataFacadeService);

  sessions = signal<PatientSessionModel[]>([]);

  targets = signal<PatientTargetModel[]>([]);
  behaviors = signal<PatientBehaviorModel[]>([]);
  programs = signal<PatientProgramModel[]>([]);
  antecedents: PatientBehaviorAntecedentModel[] = [];
  consequences: PatientBehaviorConsequenceModel[] = [];
  locations: PatientBehaviorLocationModel[] = [];
  responses: PatientBehaviorResponseModel[] = [];
  severities: PatientBehaviorSeverityModel[] = [];
  defaultPromptCodes: PromptCodeModel[] = [];

  constructor(
    protected supervisorApiService: SupervisorApiService,
    protected supervisorHelperService: SupervisorHelperService,
    protected patientSessionService: PatientSessionService,
    protected staffApiService: StaffApiService
  ) {}

  loadBaseData(patientID: number, finishedLoading?: () => void) {
    console.log('loadBaseData from sessions called');

    this.isLoading = true;

    const getPatientObservable = this.staffApiService
      .GetPatientInfo({
        id: Number(patientID),
      })
      .pipe(catchError((error) => of({ error: true, details: error })));

    const getSessionsObservable = this.staffApiService
      .GetListPatientSessions({
        patientID: Number(patientID),
      })
      .pipe(catchError((error) => of({ error: true, details: error })));

    const initialSubscription = forkJoin([
      getPatientObservable,
      getSessionsObservable,
    ]).subscribe(([patient, sessions]) => {
      if ('error' in patient) {
        console.log('Error fetching patient:', patient.details);
      } else {
        this.selectedPatient = patient as PatientModel;
      }

      if ('error' in sessions) {
        console.log('Error fetching sessions:', sessions.details);
      } else {
        const normalizedSessions = (sessions as PatientSessionModel[]).map(
          (session) => ({
            ...session,
            PatientID: session.PatientID ?? patientID,
          })
        );
        this.sessions.set(normalizedSessions);
      }

      this.isLoading = false;
      if (finishedLoading) {
        finishedLoading();
      }
    });

    this.destroyRef.onDestroy(() => {
      initialSubscription.unsubscribe();
    });

    const optionalCalls: Observable<any>[] = [
      this.patientDataFacade
        .getTargets(Number(patientID), {
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.staffApiService
        .GetPatientBehaviors({
          patientID: Number(patientID),
          onlyActive: true,
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.patientDataFacade
        .getPrograms(Number(patientID), {
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.staffApiService
        .GetPatientBehaviorAntecedents({
          patientID: Number(patientID),
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.staffApiService
        .GetPatientBehaviorConsequences({
          patientID: Number(patientID),
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.staffApiService
        .GetPatientBehaviorLocations({
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.staffApiService
        .GetPatientBehaviorResponses({
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.staffApiService
        .GetPatientBehaviorSeverities({
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true, details: error }))),
      this.supervisorApiService
        .GetPromptCodes()
        .pipe(catchError((error) => of({ error: true, details: error }))),
    ];

    const optionalSubscription = forkJoin(optionalCalls).subscribe(
      ([
        targets,
        behaviors,
        programs,
        antecedents,
        consequences,
        locations,
        responses,
        severities,
        promptCodes,
      ]) => {
        if ('error' in targets) {
          console.log('Error fetching targets:', targets.details);
        } else {
          this.targets.set(targets as PatientTargetModel[]);
        }
        if ('error' in behaviors) {
          console.log('Error fetching behaviors:', behaviors.details);
        } else {
          this.behaviors.set(behaviors as PatientBehaviorModel[]);
        }
        if ('error' in programs) {
          console.log('Error fetching programs:', programs.details);
        } else {
          this.programs.set(programs as PatientProgramModel[]);
        }
        if ('error' in antecedents) {
          console.log('Error fetching antecedents:', antecedents.details);
        } else {
          this.antecedents = antecedents as PatientBehaviorAntecedentModel[];
        }
        if ('error' in consequences) {
          console.log('Error fetching consequences:', consequences.details);
        } else {
          this.consequences = consequences as PatientBehaviorConsequenceModel[];
        }
        if ('error' in locations) {
          console.log('Error fetching locations:', locations.details);
        } else {
          this.locations = locations as PatientBehaviorLocationModel[];
        }
        if ('error' in responses) {
          console.log('Error fetching responses:', responses.details);
        } else {
          this.responses = responses as PatientBehaviorResponseModel[];
        }
        if ('error' in severities) {
          console.log('Error fetching severities:', severities.details);
        } else {
          this.severities = severities as PatientBehaviorSeverityModel[];
        }
        if ('error' in promptCodes) {
          console.log('Error fetching prompt codes:', promptCodes.details);
        } else {
          this.defaultPromptCodes = promptCodes as PromptCodeModel[];
        }
      }
    );

    this.destroyRef.onDestroy(() => {
      optionalSubscription.unsubscribe();
    });
  }
}
